import { DATA_SOURCES, UUID_NAMESPACE } from '@/config/constants';
import { formatAddedOn, pluralDimensionToSingle } from './sourceDataShaping';
import {
    getInstagramData,
    getInstagramHashtagsByName,
} from '../api/instagram.api';
import createSourceDataHook from './createSourceDataHook';
import { getPriceSegmentationHealthStat } from 'state/utils';
import { useQuery } from 'react-query';
import { indexEntities } from 'utils/entities';
import { shapeFlavors } from 'utils/helpers';
import { v5 as uuid } from 'uuid';

// Keep a stable, minimal column order so mixed category/hashtag series can be charted together.
const INSTAGRAM_TIME_SERIES_COLUMNS = [
    'date',
    'real_volume',
    'hashtags_count',
    'posts_count',
    'abs_weekly_change_rank',
    'non_cum_increase_WoW',
];

const shapeInstagramTimeSeries = (timeSeries) => {
    if (!(timeSeries && timeSeries.length)) return null;
    if (timeSeries.every(({ real_volume }) => real_volume === null))
        return null;

    const header = INSTAGRAM_TIME_SERIES_COLUMNS.reduce((acc, key, idx) => {
        acc[key] = idx;
        return acc;
    }, {});

    const mappedData = timeSeries.map((record) =>
        INSTAGRAM_TIME_SERIES_COLUMNS.map((key) => record[key] ?? null),
    );

    return [header, ...mappedData];
};

const shapeRow =
    ({ type, entities, paginateBy }) =>
    (item) => {
        const { [type]: itemId } = item;
        const maybeItem = entities[itemId];
        let resolvedName;
        if (paginateBy === 'hashtags') {
            resolvedName = item.hashtag;
        } else if (paginateBy === 'brands') {
            resolvedName = item.brand_name || maybeItem?.name;
        } else {
            resolvedName = maybeItem?.name;
        }
        const maybeShapedTimeSeries = item.time_series
            ? shapeInstagramTimeSeries(item.time_series)
            : null;
        // The api layer built healthStats before the sidecar was merged, so
        // the price stat has to be added back here.
        const isBrandRow = paginateBy === 'brands';
        const pricePoint = isBrandRow
            ? getPriceSegmentationHealthStat(maybeItem?.price ?? null)
            : null;
        return {
            ...item,
            ...(pricePoint && {
                healthStats: {
                    ...item.healthStats,
                    'price point': pricePoint,
                },
                price_segmentation: pricePoint.score,
            }),
            id:
                type === 'hashtag'
                    ? uuid(item?.hashtag, UUID_NAMESPACE)
                    : itemId,
            time_series: maybeShapedTimeSeries,
            timeSeries: maybeShapedTimeSeries,
            hierarchies: maybeItem?.hierarchies,
            flavors: shapeFlavors(maybeItem?.hierarchies),
            name: resolvedName,
            depth: maybeItem?.depth,
            cluster: item.cluster ? `instagram ${item.cluster}` : item.cluster,
            addedOn: formatAddedOn(item.added_on),
            ...((paginateBy !== 'hashtags' || item.brand_name) && {
                searchField: [
                    resolvedName,
                    item.hashtag_list || item.brand_name,
                ]
                    .filter(Boolean)
                    .join(' '),
            }),
            image: maybeItem?.image,
            type,
            fetching: false,
            fetchingData: false,
        };
    };

export const queryFnInfiniteScroll = ({ queryKey, pageParam }) =>
    queryFn({ queryKey: [queryKey[0], { ...queryKey[1], page: pageParam }] });

const queryFn = async ({ queryKey }) => {
    const resp = await getInstagramData({
        data: {
            ...queryKey[1],
            include_entities: true,
        },
    });

    const shapeRowFn = shapeRow({
        type: pluralDimensionToSingle[queryKey[1].paginateBy],
        entities: indexEntities(resp.entities),
        paginateBy: queryKey[1].paginateBy,
    });

    return {
        ...resp,
        data: resp.data?.map(shapeRowFn),
    };
};

const useInstagramData = createSourceDataHook({
    queryName: 'InstagramData',
    queryFn,
    seedEntitiesFor: DATA_SOURCES.INSTAGRAM,
    // Instagram's rest pages carry no placeholder, and its loading rows no id.
    placeholderOnRestPages: false,
    skeletonRowIds: false,
});

// Hook for fetching Instagram hashtags by name using the new watchlist endpoint
export const useInstagramHashtagsByName = ({
    hashtags,
    countries,
    order_by,
    ascending,
    page = 1,
    per_page = 500,
    enabled = true,
    placeholderData,
}) => {
    const firstPageQuery = useQuery({
        queryKey: [
            'InstagramHashtagsByName',
            {
                hashtags,
                countries,
                order_by,
                ascending,
                page,
                per_page,
            },
        ],
        queryFn: async ({ queryKey }) => {
            const [, params] = queryKey;
            const resp = await getInstagramHashtagsByName({
                data: {
                    hashtags: params.hashtags,
                    country: params.countries,
                    order_by: params.order_by,
                    ascending: params.ascending,
                    page: params.page,
                    per_page: params.per_page,
                },
            });

            // Shape the data similar to the regular Instagram data
            const shapedData = resp.data?.map((item) => ({
                ...item,
                id: uuid(item?.hashtag, UUID_NAMESPACE),
                time_series: item.time_series
                    ? shapeInstagramTimeSeries(item.time_series)
                    : null,
                timeSeries: item.time_series
                    ? shapeInstagramTimeSeries(item.time_series)
                    : null,
                name: item.hashtag,
                type: 'hashtag',
                fetching: false,
                fetchingData: false,
                cluster: item.cluster
                    ? `instagram ${item.cluster}`
                    : item.cluster,
                addedOn: formatAddedOn(item.added_on),
            }));

            return {
                ...resp,
                data: shapedData,
            };
        },
        enabled: !!(enabled && hashtags?.length && countries?.length),
        staleTime: Infinity,
        placeholderData: placeholderData?.length
            ? () => ({
                  data: placeholderData,
                  total_records: placeholderData?.length || 0,
              })
            : undefined,
    });

    let flatData;
    if (
        firstPageQuery.data &&
        firstPageQuery.data.data &&
        firstPageQuery.data.data.length
    ) {
        flatData = [...firstPageQuery.data.data];
    }

    const isLoading = firstPageQuery.isLoading;
    const isSuccess = firstPageQuery.isSuccess;

    return {
        data: flatData,
        isLoading,
        isSuccess,
        isPlaceholderData: firstPageQuery.isPlaceholderData || false,
        firstPageQuery,
        restPageQueries: [], // No pagination for this endpoint currently
    };
};

export default useInstagramData;
